import { Injectable, inject } from '@angular/core';
import { BaseUser } from '@enrollhere/classes';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';

export interface UserSearchParams {
  query?: string;
  userType?: string[];
  agencyId?: string;
  /**
   * Only users holding one of these permanent tags. The server narrows it
   * to the caller's own tags; search results never carry tags themselves.
   */
  filterByTags?: string[];
  size?: number;
  from?: number;
}

export interface UserSearchResult {
  users: BaseUser[];
  total: number;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly api = inject(EnrollhereApiService);

  private cache = new Map<string, BaseUser>();
  private pending = new Map<string, Promise<UserSearchResult>>();

  async search(params: UserSearchParams): Promise<UserSearchResult> {
    const response = await this.api.post<{
      users: unknown[];
      total: number;
    }>('user/search', {
      query: params.query,
      userType: params.userType,
      agencyId: params.agencyId,
      ...(params.filterByTags?.length
        ? { filterByTags: params.filterByTags }
        : {}),
      size: params.size ?? 20,
      from: params.from ?? 0,
    });

    const users = (response.users || []).map(u => {
      const user = BaseUser.fromJSON(u as Record<string, unknown>);
      if (user.id) this.cache.set(user.id, user);
      return user;
    });

    return { users, total: response.total || 0 };
  }

  async searchAll(
    params: Omit<UserSearchParams, 'size' | 'from'>
  ): Promise<BaseUser[]> {
    const pageSize = 100;
    const first = await this.search({ ...params, size: pageSize, from: 0 });
    const all = [...first.users];

    let loaded = all.length;
    while (loaded < first.total) {
      const page = await this.search({
        ...params,
        size: pageSize,
        from: loaded,
      });
      if (page.users.length === 0) break;
      all.push(...page.users);
      loaded += page.users.length;
    }

    return all;
  }

  async searchOnce(
    key: string,
    params: Omit<UserSearchParams, 'size' | 'from'>
  ): Promise<UserSearchResult> {
    const existing = this.pending.get(key);
    if (existing) return existing;

    const promise = this.searchAll(params)
      .then(users => ({ users, total: users.length }))
      .finally(() => this.pending.delete(key));
    this.pending.set(key, promise);
    return promise;
  }

  async ensureLoaded(
    key: string,
    params: Omit<UserSearchParams, 'size' | 'from'>
  ): Promise<void> {
    await this.searchOnce(key, params);
  }

  getCached(id: string): BaseUser | undefined {
    return this.cache.get(id);
  }
}
